import type { PaymentContext, PaymentProvider, SecondaryPaymentTypeDTO } from './paymentTypes';
import { getCheckoutProviderAdapter } from './providers';
import type { CheckoutProviderAdapter, InitDataContext } from './providers';

// One checkout widget on the payment page: the order's own PSP (host), or one per PSP that
// processes secondary payment types.
export interface CheckoutSection {
  id: string;
  role: 'host' | 'secondary';
  provider: PaymentProvider;
  nativeMethods: string[];
  paymentTypes: SecondaryPaymentTypeDTO[];
  initData: any;
  adapter: CheckoutProviderAdapter;
  contextOverrides: Partial<PaymentContext>;
  order: number;
}

export interface CheckoutPlan {
  sections: CheckoutSection[];
  // flat: one method list across sections with one pay button (the host adapter's usesFlatList).
  layout: 'sections' | 'flat';
}

/**
 * Sections from a payment_v2 response. Without secondary payment types it is the host section
 * alone, i.e. today's page. Host first, then secondary sections by their lowest payment type order.
 * A provider without an adapter is dropped.
 */
export function buildCheckoutPlan(data: any, ctx: InitDataContext): CheckoutPlan {
  const sections: CheckoutSection[] = [];
  const shared = { customerData: data.customerData, orderAddresses: data.orderAddresses };

  const hostAdapter = getCheckoutProviderAdapter(data.paymentServiceProvider);
  if (hostAdapter) {
    sections.push({
      id: `host:${hostAdapter.code}`,
      role: 'host',
      provider: hostAdapter.code,
      nativeMethods: [],
      paymentTypes: [],
      initData: hostAdapter.buildInitData(data, ctx),
      adapter: hostAdapter,
      contextOverrides: {},
      order: -Infinity,
    });
  } else {
    window.clientLogger?.warn?.('payment page: no checkout for the order\'s PSP', { provider: data.paymentServiceProvider });
  }

  const secondaryPaymentTypes: SecondaryPaymentTypeDTO[] = data.secondaryPaymentTypes ?? [];
  const secondaryProviders: Record<string, any> = data.secondaryProviders ?? {};

  for (const [provider, providerData] of Object.entries(secondaryProviders)) {
    const adapter = getCheckoutProviderAdapter(provider);
    const paymentTypes = secondaryPaymentTypes.filter((type) => type.provider === provider).sort((a, b) => a.order - b.order);
    if (!adapter) {
      window.clientLogger?.warn?.('payment page: secondary payment types of an unknown provider dropped', { provider });
      continue;
    }
    if (!paymentTypes.length) continue;

    const nativeMethods = paymentTypes.map((type) => type.nativeType);
    const initData = adapter.buildInitData({ ...providerData, ...shared }, { ...ctx, sepaPositionVariant: '', reorderedPaymentMethods: false });
    sections.push({
      id: `secondary:${adapter.code}`,
      role: 'secondary',
      provider: adapter.code,
      nativeMethods,
      paymentTypes,
      initData: adapter.applyAllowList(initData, nativeMethods),
      adapter,
      contextOverrides: adapter.contextOverrides(),
      order: Math.min(...paymentTypes.map((type) => type.order)),
    });
  }

  sections.sort((a, b) => a.order - b.order);
  const host = sections.find((section) => section.role === 'host');
  return { sections, layout: host?.adapter.usesFlatList ? 'flat' : 'sections' };
}
